import {openDeleteDialog, showNotification} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isEmpty, isNil, isNotNil} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {
  useActivateCheckoutProfileRulesMutation,
  useDeactivateCheckoutProfileRulesMutation,
  useDeleteCheckoutProfileRuleMutation,
  useSwapCheckoutProfileRulePriorityMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {CHECKOUT_DETAIL_ACTION_KEYS} from '../constants/CHECKOUT_DETAIL_ACTION_KEYS';
import {CHECKOUT_GRID_ACTION_KEYS} from '../constants/CHECKOUT_GRID_ACTION_KEYS';
import {handleCheckoutApiError} from '../utils/handleCheckoutApiError';

// The backend sends the neighbour ids as hidden columns; the visible row order may be filtered.
const PREDEFINED_COLUMNS = ['ruleAboveId', 'ruleBelowId'] as const;

export function useCheckoutRuleActions(profile?: {id: string; branchId: string}) {
  const navigate = useNavigate();
  const [activateCheckoutProfileRules] = useActivateCheckoutProfileRulesMutation();
  const [deactivateCheckoutProfileRules] = useDeactivateCheckoutProfileRulesMutation();
  const [deleteCheckoutProfileRule] = useDeleteCheckoutProfileRuleMutation();
  const [swapCheckoutProfileRulePriority] = useSwapCheckoutProfileRulePriorityMutation();
  const context = new URLSearchParams();

  if (isNotNil(profile)) {
    context.set('checkoutProfileId', profile.id);
    context.set('branchId', profile.branchId);
    context.set('returnProfileId', profile.id);
  }

  const openDetail = (actionKey: string, rowId: string, sourceSearchParams: string) => {
    const params = new URLSearchParams(sourceSearchParams);

    if (isNotNil(profile)) {
      params.set('returnProfileId', profile.id);
    }

    const path = composePath(settingsRoutes.checkoutAssignmentRuleDetail, {
      params: {id: rowId},
      isSearchParamsPreserved: false,
      queryParams: params.toString(),
    });

    if (actionKey === 'detail_newTab') {
      window.open(path);
    } else {
      navigate(path);
    }
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    sourceSearchParams,
    getColumnData,
    refreshData,
    deselectAll,
  }) => {
    if (CHECKOUT_DETAIL_ACTION_KEYS.includes(actionKey)) {
      if (!isString(rowId)) {
        return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
      }
      return openDetail(actionKey, rowId, sourceSearchParams);
    }

    const ruleIds = isArray(rowId) ? rowId : [rowId];
    const handleSuccess = (message: string) => {
      showNotification.success(message);
      refreshData();
      deselectAll();
    };
    const swapPriority = (rule: string, neighbourColumn: (typeof PREDEFINED_COLUMNS)[number]) => {
      const neighbour = getColumnData<string>(neighbourColumn)?.value;

      if (isNil(neighbour) || isEmpty(neighbour)) {
        return logError(`Missing ${neighbourColumn} for checkout rule ${actionKey}`);
      }

      return swapCheckoutProfileRulePriority({
        checkoutProfileRuleIds:
          neighbourColumn === 'ruleAboveId' ? [rule, neighbour] : [neighbour, rule],
      })
        .unwrap()
        .then(() => handleSuccess(i18n.t('general.notifications.successfullyUpdated')))
        .catch(handleCheckoutApiError);
    };

    await match({actionKey, rowId})
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.setAsActive}, () =>
        activateCheckoutProfileRules({checkoutProfileRuleIds: ruleIds})
          .unwrap()
          .then(() => handleSuccess(i18n.t('general.notifications.successfullyUpdated')))
          .catch(handleCheckoutApiError)
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.setAsInactive}, () =>
        deactivateCheckoutProfileRules({checkoutProfileRuleIds: ruleIds})
          .unwrap()
          .then(() => handleSuccess(i18n.t('general.notifications.successfullyUpdated')))
          .catch(handleCheckoutApiError)
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.delete, rowId: Pattern.string}, (params) =>
        openDeleteDialog({
          onConfirm: () =>
            deleteCheckoutProfileRule({checkoutProfileRuleId: params.rowId})
              .unwrap()
              .then(() => handleSuccess(i18n.t('general.notifications.successfullyDeleted')))
              .catch(handleCheckoutApiError),
        })
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.moveUp, rowId: Pattern.string}, (params) =>
        swapPriority(params.rowId, 'ruleAboveId')
      )
      .with({actionKey: CHECKOUT_GRID_ACTION_KEYS.moveDown, rowId: Pattern.string}, (params) =>
        swapPriority(params.rowId, 'ruleBelowId')
      )
      .with(
        {actionKey: CHECKOUT_GRID_ACTION_KEYS.delete},
        {actionKey: CHECKOUT_GRID_ACTION_KEYS.moveUp},
        {actionKey: CHECKOUT_GRID_ACTION_KEYS.moveDown},
        () => logError(`Bulk invocation of single-row action ${actionKey} is not implemented`)
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return {
    actionCallback,
    predefinedColumns: PREDEFINED_COLUMNS,
    onCreate: () =>
      navigate(
        composePath(settingsRoutes.checkoutAssignmentRuleCreate, {
          isSearchParamsPreserved: false,
          queryParams: context.toString(),
        })
      ),
  };
}
